<template>
    <div v-if="shouldRender && hasAnyPermission(`${type}.delete`)">
        <jet-confirmation-modal :closeable="false" :show="showConfirmation" type="danger" @close="close">
            <template #title>
                <slot name="title">
                    {{ trans(`${type}s.modal.delete.title`) }}
                </slot>
            </template>

            <template #content>
                <slot name="content">
                    <div v-text="trans(`${type}s.modal.delete.content.question`)" />
                    <div v-if="hasLinkedItems" v-text="trans(`${type}s.modal.delete.content.linked_items`)" />
                    <div v-if="stakeholder" class="my-2 text-center">
                        <b>"{{ stakeholder.name }}"</b>
                    </div>
                    <jet-textarea
                        v-model="form.reason"
                        :horizontal="false"
                        :invalid-feedback="form.errors.reason"
                        :label="trans(`${type}s.modal.delete.reason.label`)"
                        :placeholder="trans(`${type}s.modal.delete.reason.placeholder`)"
                        :required="true"
                        :rows="3"
                    />
                </slot>
            </template>

            <template #footer>
                <slot name="footer">
                    <jet-button
                        :destructive="true"
                        :disabled="!form.isDirty"
                        :isProcessing="form.processing"
                        variant="secondary"
                        @click.stop="deleteStakeholder"
                    >
                        {{ trans('general.action.delete') }}
                    </jet-button>
                </slot>
            </template>
        </jet-confirmation-modal>
        <dialog-modal :show="showDeletionProcessSelection" type="danger">
            <template #title>
                <slot name="title">
                    {{ trans(`${type}s.modal.delete.title`) }}
                </slot>
            </template>
            <slot name="content">
                <div
                    v-html="
                        trans(`${type}s.modal.delete.selection.content`, {
                            stakeholder: stakeholder?.name,
                        })
                    "
                />
                <jet-radio-group
                    v-model="selectedDeletionProcess"
                    :label="trans(`${type}s.modal.delete.selection.label`)"
                    :options="deletionProcessOptions"
                    class="mt-4 mb-0"
                />
            </slot>
            <template #footer>
                <slot name="footer">
                    <jet-button variant="tertiary" @click.stop="close">
                        {{ trans('general.action.cancel') }}
                    </jet-button>
                    <jet-button color="primary" @click.stop="continueToStakeholderUpdate">
                        {{ trans('general.action.continue') }}
                    </jet-button>
                </slot>
            </template>
        </dialog-modal>
        <dialog-modal :show="showUpdateStakeholderModal" add-content-classes="modal-body-overflow-auto" type="danger">
            <template #title>
                <slot name="title">
                    {{ trans(`${type}s.modal.delete.title`) }}
                </slot>
            </template>
            <slot name="content">
                <div>
                    {{ trans(`${type}s.modal.update.content`) }}
                </div>
                <div class="mt-3">
                    {{ trans(`${type}s.modal.update.current`) }}
                </div>
                <div v-if="stakeholder" class="mb-3 text-muted" style="margin: 0 21px">
                    {{ stakeholder.name }}
                </div>
                <jet-select
                    v-model="form.stakeholder"
                    :enable-search="true"
                    :horizontal="false"
                    :invalid-feedback="form.errors[`new_${type}`]"
                    :label="trans(`${type}s.modal.update.stakeholder.label`)"
                    :options="stakeholders"
                    :required="true"
                />
                <jet-textarea
                    v-model="form.reason"
                    :horizontal="false"
                    :invalid-feedback="form.errors.reason"
                    :label="trans(`${type}s.modal.delete.reason.label`)"
                    :placeholder="trans(`${type}s.modal.delete.reason.placeholder`)"
                    :required="true"
                    :rows="3"
                />
                <div>
                    <small><em class="text-danger">*</em> {{ trans('general.required_fields') }}</small>
                </div>
            </slot>
            <template #footer>
                <slot name="footer">
                    <jet-button variant="tertiary" @click.stop="close">
                        {{ trans('general.action.cancel') }}
                    </jet-button>
                    <jet-button :disabled="!form.isDirty" :is-processing="form.processing" color="danger" @click.stop="deleteStakeholder">
                        {{ trans('general.action.confirm') }}
                    </jet-button>
                </slot>
            </template>
        </dialog-modal>
    </div>
</template>
<script setup>
import JetButton from '@/Jetstream/Button.vue';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal.vue';
import JetTextarea from '@/Jetstream/Textarea.vue';
import { eventBus, events } from '@/eventBus.js';
import JetRadioGroup from '@/Jetstream/RadioGroup.vue';
import JetSelect from '@/Jetstream/Select.vue';
import { $http } from '@/bootstrap.js';
import DialogModal from '@/Jetstream/DialogModal.vue';
import { useForm } from '@inertiajs/vue3';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { trans } from '@/mixins/i18n.js';

const shouldRender = ref(false);
const showConfirmation = ref(false);
const showDeletionProcessSelection = ref(false);
const showUpdateStakeholderModal = ref(false);
const stakeholder = ref(null);
const type = ref(null);
const selectedDeletionProcess = ref('update');
const stakeholders = ref([]);

const emit = defineEmits(['deleted']);

const form = useForm({
    reason: null,
    stakeholder: null,
});

const deletionProcessOptions = computed(() => {
    return [
        {
            label: trans(`${type.value}s.modal.delete.option.update`),
            value: 'update',
        },
        {
            label: trans(`${type.value}s.modal.delete.option.delete`),
            value: 'delete',
        },
    ];
});
const hasLinkedItems = computed(() => {
    return stakeholder.value?.assetsCount > 0 || stakeholder.value?.openWorkordersCount > 0;
});

const deleteStakeholder = () => {
    form.transform((data) => {
        const stakeholderType = `new_${type.value}`;

        // Ensure that the new stakeholder property is not set
        delete data[stakeholderType];

        // Set the property if needed
        if (hasLinkedItems.value && selectedDeletionProcess.value === 'update') {
            data[stakeholderType] = data.stakeholder;
        }

        delete data.stakeholder;
        return data;
    }).delete(
        route(`${type.value}s.destroy`, {
            id: stakeholder.value?.id,
        }),
        {
            onSuccess: () => {
                close();
                emit('deleted', stakeholder.value);
            },
        },
    );
};

const close = () => {
    showConfirmation.value = false;
    showDeletionProcessSelection.value = false;
    showUpdateStakeholderModal.value = false;
    selectedDeletionProcess.value = 'update';

    setTimeout(() => {
        shouldRender.value = false;
    }, 300);
};
const reset = () => {
    form.reset();
};
const continueToStakeholderUpdate = () => {
    loadStakeholders();
    showDeletionProcessSelection.value = false;
    showUpdateStakeholderModal.value = selectedDeletionProcess.value === 'update';
    showConfirmation.value = selectedDeletionProcess.value === 'delete';
};
const loadStakeholders = () => {
    $http.get(route(`${type.value}s.index`)).then((response) => {
        stakeholders.value = response.data.data
            .filter((stakeholder) => stakeholder.id !== stakeholder.value?.id)
            .map((stakeholder) => ({
                label: stakeholder.name,
                value: stakeholder.id,
            }));
    });
};
onMounted(() => {
    eventBus.$on([events.openConfirmDeleteStakeholderModal], (data) => {
        stakeholder.value = data?.stakeholder || null;
        type.value = data.type;

        shouldRender.value = true;
        reset();

        nextTick(() => {
            showConfirmation.value = !hasLinkedItems.value;
            showDeletionProcessSelection.value = hasLinkedItems.value;
        });
    });
});

onBeforeUnmount(() => {
    eventBus.$off([events.openConfirmDeleteStakeholderModal]);
});
</script>
<style scoped>
:deep(.dropdown-menu) {
    max-height: 11rem;
}
</style>
